/**
 * enqueue-report.ts — convenience: putReport + outbox.enqueue in one call.
 *
 * Used by the UI layer to do the complete local-first write without
 * importing both services separately.
 */

import { putReport } from "./reports-local.js";
import { enqueue } from "./outbox.js";
import type { LocalReport, SyncInformeItem } from "../types.js";

/**
 * Write a report to the local store AND enqueue it for /sync.
 * The estado transitions to 'en_cola' on enqueue.
 *
 * updatedAt is stamped ONCE here and passed to both putReport and the
 * SyncInformeItem so the reports store and the outbox entry carry the
 * same last-write-wins timestamp anchor (ADR-7).
 */
export async function enqueueReport(
  input: Omit<LocalReport, "estado"> & { estado?: LocalReport["estado"] },
): Promise<void> {
  // Stamp once — both the local record and the outbox item must share this value.
  const updatedAt = new Date().toISOString();

  const record: LocalReport = {
    ...input,
    estado: "en_cola",
    updatedAt,
  };
  // Pass the shared updatedAt so putReport does not re-stamp its own.
  await putReport(record, updatedAt);

  const syncItem: SyncInformeItem = {
    localId: record.localId,
    serverId: record.serverId,
    tipo: record.tipo,
    clienteId: record.clienteId,
    cliente: record.cliente,
    parentId: record.parentLocalId,
    estado: record.estado, // "en_cola" — backend /sync itemSchema requires it
    payload: record.payload,
    updatedAt, // same stamp — reports store and outbox are consistent
  };
  await enqueue(syncItem);
}
